import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { SELECTION_STYLES } from "../utils";

// Room options organized logically for grid display
const ROOM_OPTIONS_ORDERED = [
  // Row 1: Studios and 1-room
  { slug: "studyo", label: "Stüdyo" },
  { slug: "1", label: "1" },
  { slug: "1-1", label: "1+1" },
  { slug: "15-1", label: "1.5+1" },
  // Row 2: 2-room variants
  { slug: "2-0", label: "2+0" },
  { slug: "2-1", label: "2+1" },
  { slug: "2-2", label: "2+2" },
  { slug: "25-1", label: "2.5+1" },
  // Row 3: 3-room variants
  { slug: "3-0", label: "3+0" },
  { slug: "3-1", label: "3+1" },
  { slug: "3-2", label: "3+2" },
  { slug: "35-1", label: "3.5+1" },
  // Row 4: 4-room variants
  { slug: "4-0", label: "4+0" },
  { slug: "4-1", label: "4+1" },
  { slug: "4-2", label: "4+2" },
  { slug: "4-3", label: "4+3" },
  // Row 5: 5-room variants
  { slug: "5-0", label: "5+0" },
  { slug: "5-1", label: "5+1" },
  { slug: "5-2", label: "5+2" },
  { slug: "5-3", label: "5+3" },
  // Row 6: 6+ rooms
  { slug: "6-1", label: "6+1" },
  { slug: "6-2", label: "6+2" },
  { slug: "7-1", label: "7+1" },
  { slug: "9-", label: "9+" },
];

// Popular room types to highlight
const POPULAR_ROOMS = ["1-1", "2-1", "3-1", "4-1"];

/**
 * RoomSelector - Grid-based room selection component with collapsible options
 */
export function RoomSelector({ selectedValues, onToggle, isDark }) {
  // Check if any non-popular option is selected to auto-expand
  const hasNonPopularSelected = ROOM_OPTIONS_ORDERED.some(
    opt => !POPULAR_ROOMS.includes(opt.slug) && selectedValues.has(opt.slug)
  );
  const [showAll, setShowAll] = useState(hasNonPopularSelected);

  // Use inline styles for selected state to ensure visibility
  const getButtonStyle = (isSelected, isPopular) => {
    if (isSelected) {
      return SELECTION_STYLES.selected;
    }
    if (isPopular) {
      return isDark ? SELECTION_STYLES.popularDark : SELECTION_STYLES.popularLight;
    }
    return isDark ? SELECTION_STYLES.defaultDark : SELECTION_STYLES.defaultLight;
  };

  // Non-popular options count for badge
  const nonPopularSelectedCount = Array.from(selectedValues).filter(
    slug => !POPULAR_ROOMS.includes(slug)
  ).length;

  return (
    <div className="space-y-4">
      {/* Popular options - always visible */}
      <div>
        <div className={`text-xs font-medium mb-2 ${isDark ? "text-white/50" : "text-black/50"}`}>
          Popüler
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Popüler oda seçenekleri">
          {ROOM_OPTIONS_ORDERED.filter(opt => POPULAR_ROOMS.includes(opt.slug)).map(({ slug, label }) => {
            const isSelected = selectedValues.has(slug);
            return (
              <button
                key={slug}
                type="button"
                onClick={() => onToggle(slug)}
                style={getButtonStyle(isSelected, true)}
                className="px-4 py-2 rounded-full text-sm transition-all hover:opacity-80"
                aria-pressed={isSelected}
                aria-label={`${label} oda${isSelected ? " (seçili)" : ""}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Collapsible all options */}
      <div>
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          className={`flex items-center gap-2 text-xs font-medium mb-2 transition-colors ${
            isDark ? "text-white/50 hover:text-white/70" : "text-black/50 hover:text-black/70"
          }`}
          aria-expanded={showAll}
        >
          {showAll ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          <span>Tüm Seçenekler</span>
          {!showAll && nonPopularSelectedCount > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-emerald-500 text-white">
              +{nonPopularSelectedCount}
            </span>
          )}
        </button>

        {showAll && (
          <div className="grid grid-cols-4 gap-2" role="group" aria-label="Tüm oda seçenekleri">
            {ROOM_OPTIONS_ORDERED.map(({ slug, label }) => {
              const isSelected = selectedValues.has(slug);
              const isPopular = POPULAR_ROOMS.includes(slug);
              return (
                <button
                  key={slug}
                  type="button"
                  onClick={() => onToggle(slug)}
                  style={getButtonStyle(isSelected, isPopular)}
                  className="py-2.5 px-2 rounded-xl text-sm transition-all hover:opacity-80"
                  aria-pressed={isSelected}
                  aria-label={`${label} oda${isSelected ? " (seçili)" : ""}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default RoomSelector;
